// src/components/MessageFileAttachments.js - Fixed to properly display filenames in historical messages
import React, { useEffect, useState } from 'react';
import './FileAttachments.css';
import { getExtensionFromMimeType } from '../fileUtils/cleanFileUtils';

/**
 * MessageFileAttachments - Optimized component for displaying file attachments in messages
 * Uses a file locator approach instead of in-app viewer for memory efficiency
 * FIXED: Added debugging and better filename handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.files - Array of file objects (metadata only)
 * @param {string} props.threadId - ID of the current thread
 * @param {string} props.role - Role of the message sender ('user' or 'assistant')
 */
export default function MessageFileAttachments({ files, threadId, role }) {
  const [processedFiles, setProcessedFiles] = useState([]);

  // Process files on component mount or when files prop changes
  useEffect(() => {
    if (!files || files.length === 0) return;
    
    // Debug log to see what properties are available in the files
    console.log(`MessageFileAttachments: Processing ${files.length} files for ${role}:`, files);
    
    // Process files to ensure all have necessary properties
    const fixedFiles = files.map((file, index) => {
      // Create a copy of the file object
      const processedFile = { ...file };
      
      // Check if the file already has a name, if not, try to find it elsewhere
      if (!processedFile.name && processedFile.originalName) {
        processedFile.name = processedFile.originalName;
      }
      
      // If still no name, use file type to generate a placeholder
      if (!processedFile.name && processedFile.type) {
        const extension = getExtensionFromMimeType(processedFile.type);
        processedFile.name = `File${index + 1}.${extension}`;
      }
      
      // Last resort - generic name
      if (!processedFile.name) {
        processedFile.name = `File ${index + 1}`;
      }
      
      return processedFile;
    });
    
    // Update state with processed files
    setProcessedFiles(fixedFiles);
  }, [files, role, threadId]);

  // Early return if no files
  if (!processedFiles || processedFiles.length === 0) {
    return null;
  }

  // Get file icon based on file type and name
  const getFileIcon = (fileType, fileName = '') => {
    if (!fileType && !fileName) return '📎';
    
    if ((fileType && fileType.startsWith('image/')) || 
        (fileName && fileName.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i))) {
      return '🖼️';
    }
    if ((fileType && fileType === 'application/pdf') || 
        (fileName && fileName.endsWith('.pdf'))) {
      return '📄';
    }
    if ((fileType && (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType === 'text/csv')) || 
        (fileName && fileName.match(/\.(xlsx|xls|csv|ods)$/i))) {
      return '📊';
    }
    if ((fileType && fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') || 
        (fileName && fileName.endsWith('.docx'))) {
      return '📝';
    }
    if ((fileType && fileType.startsWith('text/')) || 
        (fileName && fileName.match(/\.(txt|js|py|java|cpp|css|html|json|md)$/i))) {
      return '📝';
    }
    return '📎';
  };

  // Format file size in human-readable format
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Get readable file type for display
  const getDisplayFileType = (fileType, fileName) => {
    // First try to determine type from the filename extension if available
    if (fileName) {
      const extension = fileName.split('.').pop()?.toLowerCase();
      if (extension) {
        switch (extension) {
          case 'pdf': return 'PDF';
          case 'docx': return 'DOCX';
          case 'doc': return 'DOC';
          case 'xlsx': return 'XLSX';
          case 'xls': return 'XLS';
          case 'csv': return 'CSV';
          case 'txt': return 'TXT';
          case 'jpg':
          case 'jpeg': return 'JPEG';
          case 'png': return 'PNG';
          case 'gif': return 'GIF';
          case 'svg': return 'SVG';
          default: return extension.toUpperCase();
        }
      }
    }
    
    // If no extension in filename, try MIME type
    if (!fileType) return '';
    
    // Handle common MIME types
    if (fileType === 'application/pdf') return 'PDF';
    if (fileType === 'text/csv') return 'CSV';
    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'DOCX';
    if (fileType === 'application/msword') return 'DOC';
    if (fileType.includes('spreadsheet') || fileType.includes('excel')) return 'Spreadsheet';
    
    // For general MIME types, use a simplified version
    const typeParts = fileType.split('/');
    return typeParts.length > 1 ? typeParts[1].toUpperCase() : typeParts[0].toUpperCase();
  };

  // Handle locating similar files
  const handleLocateFile = (file, e) => {
    // Safely handle event
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    
    // Create a temporary file input element
    const input = document.createElement('input');
    input.type = 'file';
    
    // Set accept attribute based on file type/extension
    if (file.type) {
      input.accept = file.type;
    } else if (file.name) {
      // Extract extension from filename if type not available
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension) {
        input.accept = `.${extension}`;
      }
    }
    
    // Trigger the file picker dialog
    input.click();
    
    // Clean up
    setTimeout(() => {
      input.remove();
    }, 1000); // Delay removal to ensure dialog opens
  };

  return (
    <div className={`message-file-attachments ${role}-attachments`}>
      <div className="attachments-header">
        <span>Attached Files ({processedFiles.length})</span>
      </div>
      <div className="attachments-list">
        {processedFiles.map((file, index) => (
          <div
            key={`${threadId}-${index}-${file.name || `file-${index}`}`}
            className="attachment-item"
          >
            <div className="attachment-icon">
              <span className="file-icon">{getFileIcon(file.type, file.name)}</span>
            </div>
            <div className="attachment-details">
              <div className="attachment-name" title={file.name}>
                {file.name}
              </div>
              <div className="attachment-meta">
                <span className="attachment-size">
                  {formatFileSize(file.size)}
                </span>
                <span className="attachment-meta-separator"> • </span>
                <span className="attachment-type">
                  {getDisplayFileType(file.type, file.name)}
                </span>
              </div>
            </div>
            <button 
              className="attachment-locate-button"
              onClick={(e) => handleLocateFile(file, e)}
              title="Open file picker to locate similar files"
            >
              Locate
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}